import PropTypes from 'prop-types';
import styled from 'styled-components';

import { config } from '_config';
import { theme } from '_constants/theme';

import * as Layout from '_components/atomic/layouts';
import JoinBanner from '../JoinBanner';

export const essentialTaxTipsForNewBusinessOwnersContents = [
  'Understand Your Tax Obligations',
  'Keep Accurate Records',
  'Maximize Deductions',
  'Plan for Estimated Taxes',
  'Consider Hiring a Tax Professional',
  'Stay Informed About Tax Law Changes',
  'Separate Personal and Business Finances',
  'Tax Advantage of Tax Credits',
];

const StyledContainer = styled.div`
  position: relative;
`;

const StyledBlock = styled.div`
  position: relative;
  z-index: 1;

  ol,
  ul {
    padding-top: ${theme.spaces.regular}px;

    li {
      margin-bottom: ${theme.spaces.regular}px;

      &::marker {
        font-weight: 700;
      }
    }
  }
`;

const StyledBackgroundImageContainer = styled.div`
  position: absolute;
  top: 40%;
  width: 100%;

  img {
    width: 100%;
  }
`;

const EssentialTaxTipsForNewBusinessOwnersArticle = ({ idPrefix }) => {
  return (
    <StyledContainer>
      <Layout.Card.Text>
        Starting a new business is an exciting venture, but navigating the world
        of taxes can be daunting for new business owners. Understanding your tax
        obligations and taking advantage of available deductions can
        significantly impact your bottom line. This guide provides essential tax
        tips to help new business owners manage their finances and avoid common
        tax pitfalls.
      </Layout.Card.Text>
      <StyledBlock id={`${idPrefix}-${0}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          Understand Your Tax Obligations
        </Layout.Card.Text>
        <Layout.Card.Text>
          The first step in managing your business taxes is understanding your
          obligations. Depending on your business structure and location, you
          may need to pay federal, state, and local taxes. Common types of taxes
          include:
          <ul>
            <li>
              <b>Income Tax:</b> All businesses must file an annual income tax
              return.
            </li>
            <li>
              <b>Self-Employment Tax:</b> Sole proprietors and partners must pay
              self-employment tax to cover Social Security and Medicare.
            </li>
            <li>
              <b>Employment Tax:</b> If you have employees, you must withhold
              and pay employment taxes, including Social Security, Medicare, and
              federal income tax.
            </li>
            <li>
              <b>Sales Tax:</b> If you sell goods or services, you may be
              required to collect sales tax from customers and remit it to the
              state.
            </li>
          </ul>
        </Layout.Card.Text>
      </StyledBlock>
      <StyledBlock id={`${idPrefix}-${1}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          Keep Accurate Records
        </Layout.Card.Text>
        <Layout.Card.Text>
          Maintaining accurate and detailed records is crucial for tax purposes.
          Good record-keeping helps you track income and expenses, prepare
          accurate tax returns, and substantiate deductions in case of an audit.
          Essential records to keep include:
          <ul>
            <li>
              <b>Receipts and Invoices:</b> Document all business transactions,
              including sales, purchases, and expenses.
            </li>
            <li>
              <b>Bank Statements:</b> Keep copies of your business bank
              statements to track income and expenses.
            </li>
            <li>
              <b>Payroll Records:</b> If you have employees, maintain records of
              wages, salaries, and payroll taxes.
            </li>
            <li>
              <b>Tax Filings:</b> Keep copies of all tax returns and supporting
              documents.
            </li>
          </ul>
        </Layout.Card.Text>
      </StyledBlock>
      <StyledBlock id={`${idPrefix}-${2}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          Maximize Deductions
        </Layout.Card.Text>
        <Layout.Card.Text>
          Taking advantage of available deductions can significantly reduce your
          tax liability. Common business deductions include:
          <ul>
            <li>
              <b>Home Office Deduction:</b> If you use part of your home
              exclusively for business, you may qualify for a home office
              deduction.
            </li>
            <li>
              <b>Vehicle Expenses:</b> If you use your car for business
              purposes, you can deduct related expenses, such as mileage, gas,
              and maintenance.
            </li>
            <li>
              <b>Business Meals:</b> You can deduct 50% of the cost of
              business-related meals.
            </li>
            <li>
              <b>Supplies and Equipment:</b> Deduct the cost of office supplies,
              equipment, and software used for your business.
            </li>
            <li>
              <b>Professional Services:</b> Fees paid to accountants, attorneys,
              and other professionals are deductible.
            </li>
            <li>
              <b>Travel Expenses:</b> Deduct expenses for business-related
              travel, including airfare, lodging, and meals.
            </li>
          </ul>
        </Layout.Card.Text>
      </StyledBlock>
      <StyledBlock id={`${idPrefix}-${3}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          Plan for Estimated Taxes
        </Layout.Card.Text>
        <Layout.Card.Text>
          If you expect to owe at least $1,000 in taxes when you file your
          return, you may need to pay estimated taxes quarterly. This helps you
          avoid penalties and ensures you don&apos;t face a large tax bill at
          the end of the year. Use IRS Form 1040-ES to calculate and pay
          estimated taxes.
        </Layout.Card.Text>
      </StyledBlock>
      <StyledBlock id={`${idPrefix}-${4}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          Consider Hiring a Tax Professional
        </Layout.Card.Text>
        <Layout.Card.Text>
          Tax laws can be complex and ever-changing. Hiring a tax professional
          can help you navigate these complexities and ensure compliance with
          all tax regulations. A tax professional can also help you identify
          deductions, plan for estimated taxes, and prepare accurate tax
          returns.
        </Layout.Card.Text>
      </StyledBlock>
      <StyledBlock id={`${idPrefix}-${5}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          Stay Informed About Tax Law Changes
        </Layout.Card.Text>
        <Layout.Card.Text>
          Tax laws frequently change, and staying informed about these changes
          is essential for compliance and maximizing tax benefits. Subscribe to
          IRS updates, follow reputable tax news sources, and consult with your
          tax professional regularly to stay current on tax law changes that may
          impact your business.
        </Layout.Card.Text>
      </StyledBlock>
      <StyledBlock id={`${idPrefix}-${6}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          Separate Personal and Business Finances
        </Layout.Card.Text>
        <Layout.Card.Text>
          Maintaining separate bank accounts and credit cards for your business
          and personal finances is crucial for accurate record-keeping and tax
          reporting. This separation helps you track business expenses, avoid
          commingling funds, and substantiate deductions in case of an audit.
        </Layout.Card.Text>
      </StyledBlock>
      <StyledBlock id={`${idPrefix}-${7}`}>
        <Layout.Card.Text
          as="h1"
          size={theme.fontSizes.large}
          color={theme.colors.indigo[600]}
          boldest
          centered
          underline
        >
          Take Advantage of Tax Credits
        </Layout.Card.Text>
        <Layout.Card.Text>
          Tax credits can directly reduce your tax liability, providing
          significant savings. Common tax credits for small businesses include:
          <ul>
            <li>
              <b>Small Business Health Care Tax Credit:</b> If you provide
              health insurance to your employees, you may qualify for this
              credit.
            </li>
            <li>
              <b>Research and Development (R&D) Tax Credit:</b> If your business
              invests in research and development, you may qualify for this
              credit.
            </li>
            <li>
              <b>Work Opportunity Tax Credit:</b> This credit is available for
              businesses that hire individuals from targeted groups, such as
              veterans and long-term unemployed.
            </li>
          </ul>
        </Layout.Card.Text>
      </StyledBlock>
      <Layout.Card.Text>
        Managing your business taxes effectively is crucial for your financial
        success. By understanding your tax obligations, keeping accurate
        records, maximizing deductions, and staying informed about tax law
        changes, you can minimize your tax liability and avoid common pitfalls.
        Consider hiring a tax professional to ensure compliance and take
        advantage of all available tax benefits. At Incorpified, we provide
        expert guidance and support to help you navigate the complexities of
        business taxes and ensure your business&apos;s financial success.
      </Layout.Card.Text>
      <Layout.Card.Text>
        By following these essential tax tips, new business owners can
        confidently manage their tax obligations and set their business up for
        long-term success. Let Incorpified be your partner in achieving
        financial stability and growth.
      </Layout.Card.Text>
      <JoinBanner />
      <StyledBackgroundImageContainer>
        <picture>
          <source
            srcSet={`${config.basePath}/images/blog-arrow-logo2.svg`}
            type="image/svg+xml"
          />
          <img
            src={`${config.basePath}/images/blog-arrow-logo2.svg`}
            alt="Background Logo"
          />
        </picture>
      </StyledBackgroundImageContainer>
    </StyledContainer>
  );
};

EssentialTaxTipsForNewBusinessOwnersArticle.propTypes = {
  idPrefix: PropTypes.string,
};

export default EssentialTaxTipsForNewBusinessOwnersArticle;
